import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import UnsavedModal from '../../components/unsaved-modal/UnsavedModal';
import { SCOUT_TABS } from '../../constants';
import useBindAction from '../../hooks/useBindAction';
import useRedirectPrompt from '../../hooks/useRedirectPrompt';
import {
  getAllMessagesConsumer,
  getSelectedMessagesConsumer,
  getFinalMessagesConsumer,
  getScreenedMessagesConsumer,
  resetSessionConsumer,
  replyScoutConsumer,
  generateMessagesConsumer,
  generateMoreConsumer,
  compressMessagesConsumer,
  selectMessageConsumer,
  deleteMessageConsumer,
  likeMessageConsumer,
  getMessageLogsConsumer,
  saveMessageChangesConsumer,
  getConsumerScoutData,
  getMessagesByTab,
} from '../../redux/consumer/scout/consumerScoutActions';
import { changeGeneratingStatus, changeScoutTab, setEditableMessage, setIsDirty } from '../../redux/scout/scoutReducer';
import GenerationSection from '../../scout-new/components/generation-section/GenerationSection';
import MessagesOutput from '../../scout-new/components/message-output/MessagesOutput';
import ConsumerMessagesFilters from './components/consumer-messages-filters/ConsumerMessagesFilters';
import Header from './components/header/Header';
import { uploadFile } from '../../redux/scout/scoutActions';
import ConsumerLayout from '../consumer-layout/ConsumerLayout';
import { clearConsumerProjectStatus } from '../../redux/global/globalActions';
import useConsumerProjectStatusTracker from '../../hooks/useConsumerProjectStatusTracker';
import useScoutTabs from '../../scout-new/hooks/useScoutTabs';
import CommonTabs from '../../components/common-tabs/CommonTabs';
import CommonTabPanel from '../../components/common-tabs/CommonTabPanel';

import scss from './Scout.module.scss';

function Scout() {
  const dispatch = useDispatch();

  const { projectName, projectPassword } = useSelector((state) => state.consumer.scout);
  const scoutTab = useSelector((state) => state.scout.scoutTab);
  const { editableMessage, isDirty } = useSelector((state) => state.scout);
  const { triggerAction, bindAction, resetAction } = useBindAction(isDirty, () => handleShowModal());
  const [showPrompt, confirmNavigation, cancelNavigation] = useRedirectPrompt(!!editableMessage.id && isDirty);

  const scoutTabs = useScoutTabs({ isConsumer: true });

  const [isShowGenerationForm, setIsShowGenerationForm] = useState(true);
  const [isShowUnsavedModal, setIsShowUnsavedModal] = useState(false);

  const handleChangeTabs = useCallback(
    (_, newTab) => {
      bindAction(() => dispatch(changeScoutTab(newTab)));
    },
    [bindAction, dispatch],
  );

  const handleSearchAllMessages = () => {
    dispatch(getAllMessagesConsumer());
  };

  const handleSearchSelectedMessages = () => {
    dispatch(getSelectedMessagesConsumer());
  };

  const handleSearchFinalMessages = () => {
    dispatch(getFinalMessagesConsumer());
  };

  const handleSearchScreenedMessages = () => {
    dispatch(getScreenedMessagesConsumer());
  };

  const MESSAGES_LISTS = {
    [SCOUT_TABS.ALL_MESSAGES]: handleSearchAllMessages,
    [SCOUT_TABS.SELECTED_MESSAGES]: handleSearchSelectedMessages,
    [SCOUT_TABS.FINAL_MESSAGES]: handleSearchFinalMessages,
    [SCOUT_TABS.SCREENED_MESSAGES]: handleSearchScreenedMessages,
  };

  const handleShowModal = () => {
    setIsShowUnsavedModal(true);
  };

  const handleCancelModal = () => {
    setIsShowUnsavedModal(false);
    resetAction(null);

    if (showPrompt) {
      cancelNavigation();
    }
  };

  const handleDontSave = () => {
    setIsShowUnsavedModal(false);
    dispatch(setEditableMessage({ id: null, message: null }));
    dispatch(setIsDirty(false));

    if (triggerAction) {
      triggerAction();
    }

    if (showPrompt) {
      confirmNavigation();
    }
  };

  const handleSaveModal = async () => {
    setIsShowUnsavedModal(false);
    await dispatch(saveMessageChangesConsumer({ scoutTab }));

    if (triggerAction) {
      triggerAction();
    }

    if (showPrompt) {
      confirmNavigation();
    }
  };

  const handleResetSession = () => {
    dispatch(clearConsumerProjectStatus({ projectName, projectPassword }));
  };

  const handleSetGeneratingStatus = (status) => {
    dispatch(changeGeneratingStatus(status));
  };

  const handleUpdateForm = () => {
    dispatch(getConsumerScoutData({ projectName, projectPassword }));
    dispatch(getMessagesByTab());
  };

  useConsumerProjectStatusTracker({
    onChangeStatus: handleSetGeneratingStatus,
    onInitialUpdate: handleUpdateForm,
    selector: (state) => state.consumer.scout,
  });

  useEffect(() => {
    return () => {
      handleResetSession();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener('beforeunload', handleResetSession);

    return () => {
      window.removeEventListener('beforeunload', handleResetSession);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ConsumerLayout Header={Header()}>
      <GenerationSection
        isShowGenerationForm={isShowGenerationForm}
        onShowGenerationForm={setIsShowGenerationForm}
        onReply={replyScoutConsumer}
        onGenerateMessages={generateMessagesConsumer}
        onGenerateMore={generateMoreConsumer}
        onCompressMessages={compressMessagesConsumer}
        onResetSession={resetSessionConsumer}
        onFileUpload={uploadFile}
      />

      <CommonTabs tabs={scoutTabs} value={scoutTab} onChange={handleChangeTabs} isFullWidth className={scss.tabs} />

      {Object.entries(MESSAGES_LISTS).map(([tab, searchFunction], index) => (
        <CommonTabPanel value={scoutTab} index={index} key={tab} className={scss.tabPanel}>
          <div className={scss.outputWrapper}>
            <ConsumerMessagesFilters />

            <MessagesOutput
              bindNextAction={bindAction}
              onSearch={searchFunction}
              onSelect={selectMessageConsumer}
              onDelete={deleteMessageConsumer}
              onLike={likeMessageConsumer}
              onSaveMessage={saveMessageChangesConsumer}
              getMessageLogs={getMessageLogsConsumer}
              maxHeight={isShowGenerationForm ? 'calc(100vh - 544px)' : 'calc(100vh - 344px)'}
            />
          </div>
        </CommonTabPanel>
      ))}

      <UnsavedModal
        isOpen={isShowUnsavedModal}
        onCancel={handleCancelModal}
        onClose={handleCancelModal}
        onDontSave={handleDontSave}
        onSave={handleSaveModal}
      />
    </ConsumerLayout>
  );
}

export default Scout;
